import { useId, useRef, useState, type ReactNode } from "react";
import { cn } from "../../utils/cn";
import { Field } from "./Field";
import { useClickOutside } from "../../hooks/useClickOutside";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  label?: string;
  helper?: string;
  error?: string;
  placeholder?: string;
  value?: string | null;
  onChange?: (value: string) => void;
  options: SelectOption[];
  disabled?: boolean;
  required?: boolean;
  searchable?: boolean;
  clearable?: boolean;
  /** Optional icon rendered before the trigger label. */
  prefix?: ReactNode;
  className?: string;
}

export function Select({
  label,
  helper,
  error,
  placeholder = "Select…",
  value,
  onChange,
  options,
  disabled,
  required,
  searchable = false,
  clearable = false,
  prefix,
  className,
}: SelectProps) {
  const id = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  useClickOutside(rootRef, () => {
    setOpen(false);
    setHighlightedIndex(-1);
  }, open);

  const checkPosition = () => {
    if (rootRef.current) {
      const rect = rootRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      setDropUp(spaceBelow < 220 && spaceAbove > spaceBelow);
    }
  };

  const selected = options.find((o) => o.value === value);
  const filtered = query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) {
        checkPosition();
        setOpen(true);
        setHighlightedIndex(0);
      } else {
        setHighlightedIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : prev));
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) {
        checkPosition();
        setOpen(true);
        setHighlightedIndex(filtered.length - 1);
      } else {
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
      }
    } else if (e.key === "Enter" || e.key === " ") {
      if (open && highlightedIndex >= 0 && highlightedIndex < filtered.length) {
        e.preventDefault();
        const opt = filtered[highlightedIndex];
        if (!opt.disabled) {
          onChange?.(opt.value);
          setOpen(false);
          setHighlightedIndex(-1);
        }
      } else if (!open && e.key === "Enter") {
        e.preventDefault();
        checkPosition();
        setOpen(true);
        setHighlightedIndex(0);
      }
    } else if (e.key === "Escape") {
      if (open) {
        e.preventDefault();
        setOpen(false);
        setHighlightedIndex(-1);
      }
    }
  };

  return (
    <Field label={label} htmlFor={id} helper={helper} error={error} required={required}>
      <div ref={rootRef} className={cn("relative min-w-[140px]", className)} onKeyDown={handleKeyDown}>
        <button
          type="button"
          id={id}
          disabled={disabled}
          onClick={() => {
            if (!open) checkPosition();
            setOpen((v) => !v);
            setQuery("");
            setHighlightedIndex(-1);
          }}
          className={cn(
            "flex h-9 w-full cursor-pointer items-center justify-between gap-2 rounded-md border bg-background-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40 disabled:cursor-not-allowed disabled:bg-background-100 disabled:text-foreground-500",
            error ? "border-danger-300" : "border-background-300",
            open && "ring-2 ring-primary-400/40"
          )}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className={cn("flex min-w-0 items-center gap-2 truncate", !selected && "text-foreground-400")}>
            {prefix}
            <span className="truncate font-normal text-foreground-900">{selected ? selected.label : placeholder}</span>
          </span>
          <span className="flex flex-none items-center gap-1">
            {clearable && selected ? (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange?.("");
                }}
                className="flex h-5 w-5 cursor-pointer items-center justify-center rounded text-foreground-400 hover:text-foreground-700"
              >
                <i className="ri-close-line text-sm" />
              </span>
            ) : null}
            <i className="ri-arrow-down-s-line text-base text-foreground-400" />
          </span>
        </button>

        {open ? (
          <div
            className={cn(
              "absolute left-0 z-50 min-w-full w-max max-w-sm rounded-md border border-background-200 bg-background-50 shadow-xl shadow-foreground-950/10",
              dropUp ? "bottom-full mb-1" : "top-full mt-1"
            )}
          >
            {searchable ? (
              <div className="border-b border-background-100 p-1.5">
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setHighlightedIndex(0);
                  }}
                  placeholder="Type to search…"
                  className="h-8 w-full rounded-md border border-background-200 bg-background-50 px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40"
                />
              </div>
            ) : null}
            <div ref={listRef} className="max-h-60 overflow-y-auto py-1 rounded-md" role="listbox">
              {filtered.length === 0 ? (
                <div className="px-3 py-2 text-xs text-foreground-500">No options found</div>
              ) : (
                filtered.map((option, index) => (
                  <button
                    type="button"
                    key={option.value}
                    role="option"
                    aria-selected={option.value === value}
                    disabled={option.disabled}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    onClick={() => {
                      onChange?.(option.value);
                      setOpen(false);
                      setHighlightedIndex(-1);
                    }}
                    className={cn(
                      "flex w-full cursor-pointer items-center justify-between gap-3 px-3 py-2.5 text-left text-sm whitespace-nowrap transition-colors disabled:cursor-not-allowed disabled:text-foreground-400",
                      index === highlightedIndex
                        ? "bg-primary-100/80 text-primary-900 font-medium"
                        : option.value === value
                        ? "bg-primary-50 text-primary-800 font-semibold"
                        : "hover:bg-background-100"
                    )}
                  >
                    <span>{option.label}</span>
                    {option.value === value ? <i className="ri-check-line text-primary-600 flex-none text-base" /> : null}
                  </button>
                ))
              )}
            </div>
          </div>
        ) : null}
      </div>
    </Field>
  );
}